import React, { useState, useMemo, useRef, useCallback } from 'react'

import type { FormikProps } from 'formik'

import {
  Stack,
  Box,
  Typography,
  Grid,
  GridItem,
  Skeleton,
  HStack,
  Form,
  Select,
  EmptyState,
} from '@pnld/components-web'

import {
  useDashboardCensusEducationStages,
  useDashboardCitiesByState,
} from '@/api/dashboard/queries'
import { PieChart, BarChart } from '@/components/Charts'
import SlidesDisplay from '@/components/Display/Slides'
import { stateOptions, stateToRegionMap } from '@/data/dashboard'
import { State } from '@/types/dashboard'

import { CensusMultiYearProps } from './types'

interface FilterFormValues {
  states: State[]
  cities: number[]
}

export const CensusMultiYear: React.FC<CensusMultiYearProps> = ({
  census,
  globalFilters,
}) => {
  const globalStates = globalFilters?.states || []
  const globalCities = globalFilters?.cities || []

  const initialValues: FilterFormValues = {
    states: globalStates.length > 0 ? globalStates : [],
    cities: globalCities.length > 0 ? globalCities : [],
  }

  const [formValues, setFormValues] = useState<FilterFormValues>(initialValues)
  const formRef = useRef<FormikProps<FilterFormValues> | null>(null)
  const selectRefs = useRef<Record<string, any>>({})

  const mapToBaseFilters = useCallback(
    (localStates: State[], localCities: number[]) => {
      return {
        coRegion: globalFilters?.region || undefined,
        coUf:
          localStates.length > 0
            ? localStates
            : globalFilters?.states?.length
            ? globalFilters.states
            : undefined,
        coCity:
          localCities.length > 0
            ? localCities
            : globalFilters?.cities?.length
            ? globalFilters.cities
            : undefined,
        coEntity: globalFilters?.schools?.length
          ? globalFilters.schools
          : undefined,
        tpDependency: globalFilters?.dependencies?.length
          ? globalFilters.dependencies
          : undefined,
        tpLocation: globalFilters?.locations?.length
          ? globalFilters.locations
          : undefined,
        coSphere: globalFilters?.spheres?.length
          ? globalFilters.spheres
          : undefined,
        stPnldService: globalFilters?.hasPndlService || undefined,
        coEducationStage: globalFilters?.educationStages?.length
          ? globalFilters.educationStages
          : undefined,
        coYearGrade: globalFilters?.yearGrades?.length
          ? globalFilters.yearGrades
          : undefined,
      }
    },
    [globalFilters]
  )

  const { data: cities, isLoading: isCitiesLoadingRaw } =
    useDashboardCitiesByState(
      formValues.states.length > 0 ? { coUf: formValues.states } : { coUf: [] },
      { enabled: formValues.states.length > 0 }
    )

  const isCitiesLoading = isCitiesLoadingRaw && formValues.states.length > 0

  const {
    data: censusEducationStages,
    isLoading: isCensusEducationStagesLoading,
  } = useDashboardCensusEducationStages(
    {
      nuCensusYear: census.map(c => c.nuCensusYear),
      ...mapToBaseFilters(formValues.states, formValues.cities),
    },
    { enabled: census.length > 0 }
  )

  const handleOnSubmit = (values: FilterFormValues) => {
    setFormValues(values)
  }

  const handleStatesChange = useCallback((options: any) => {
    const states = options ? options.map((o: any) => o.value) : []
    setFormValues(prev => ({ ...prev, states, cities: [] }))
    formRef.current?.setFieldValue('states', states)
    formRef.current?.setFieldValue('cities', [])
    selectRefs.current.cities?.clearValue?.()
  }, [])

  const handleCitiesChange = useCallback((options: any) => {
    const cities = options ? options.map((o: any) => o.value) : []
    setFormValues(prev => ({ ...prev, cities }))
    formRef.current?.setFieldValue('cities', cities)
  }, [])

  const availableStateOptions = useMemo(() => {
    if (globalStates.length > 0) {
      return stateOptions.filter(option => globalStates.includes(option.value))
    }

    if (globalFilters?.region) {
      return stateOptions.filter(
        option => stateToRegionMap[option.value] === globalFilters.region
      )
    }

    return stateOptions
  }, [globalStates, globalFilters?.region])

  const availableCityOptions = useMemo(() => {
    if (formValues.states.length > 0) {
      return (
        cities?.map(city => ({
          label: city.label,
          value: city.value,
        })) || []
      )
    }
    return []
  }, [formValues.states, cities])

  const isStateDisabled = globalStates.length > 0
  const isCityGlobalDisabled =
    globalCities.length > 0 || formValues.states.length === 0

  const pivotData = (
    key: 'region' | 'dependency' | 'location' | 'educationStage'
  ) => {
    type ChartRow = { name: string; [year: string]: number | string }
    const map: Record<string, ChartRow> = {}

    census.forEach(c => {
      const year = String(c.nuCensusYear)
      ;(c[key] as { label: string; value: number }[]).forEach(item => {
        if (!map[item.label]) {
          map[item.label] = { name: item.label }
        }

        map[item.label][year] = item.value
      })
    })

    return Object.values(map)
  }

  const pivotEducationStagesData = () => {
    if (!censusEducationStages || censusEducationStages.length === 0) {
      return []
    }

    const stagesMap: Record<
      string,
      {
        title: string
        yearGradesData: Record<
          string,
          { name: string; [year: string]: number | string }
        >
      }
    > = {}

    censusEducationStages.forEach(yearData => {
      const year = String(yearData.nuCensusYear)

      yearData.educationData.forEach(stage => {
        if (!stagesMap[stage.dsEducationStage]) {
          stagesMap[stage.dsEducationStage] = {
            title: stage.dsEducationStage,
            yearGradesData: {},
          }
        }

        stage.yearGrades.forEach(grade => {
          if (!stagesMap[stage.dsEducationStage].yearGradesData[grade.label]) {
            stagesMap[stage.dsEducationStage].yearGradesData[grade.label] = {
              name: grade.label,
            }
          }
          stagesMap[stage.dsEducationStage].yearGradesData[grade.label][year] =
            grade.value
        })
      })
    })

    return Object.values(stagesMap).map(stage => ({
      title: stage.title,
      data: Object.values(stage.yearGradesData),
    }))
  }

  return (
    <Stack gap={6}>
      <Grid templateColumns="repeat(3, 1fr)" gap={6}>
        <GridItem>
          <PieChart
            data={census.map(c => ({
              name: String(c.nuCensusYear),
              value: c.students,
            }))}
            centralLabel="Alunos"
            legendDirection="row"
            chartMinWidth={300}
          />
        </GridItem>
        <GridItem>
          <PieChart
            data={census.map(c => ({
              name: String(c.nuCensusYear),
              value: c.classes,
            }))}
            centralLabel="Turmas"
            legendDirection="row"
            chartMinWidth={300}
          />
        </GridItem>
        <GridItem>
          <PieChart
            data={census.map(c => ({
              name: String(c.nuCensusYear),
              value: c.schools,
            }))}
            centralLabel="Escolas"
            legendDirection="row"
            chartMinWidth={300}
          />
        </GridItem>
      </Grid>

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Typography variant="h-xs" color="brand.primary.dark_2">
          Regiões
        </Typography>

        {pivotData('region').length > 0 ? (
          <Box height={300}>
            <BarChart data={pivotData('region')} />
          </Box>
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de regiões para exibir."
            iconProps={{ name: 'chart-bar' }}
            shaped="false"
          />
        )}
      </Stack>

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Typography variant="h-xs" color="brand.primary.dark_2">
          Dependências
        </Typography>

        {pivotData('dependency').length > 0 ? (
          <Box height={300}>
            <BarChart data={pivotData('dependency')} />
          </Box>
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de dependências para exibir."
            iconProps={{ name: 'chart-bar' }}
            shaped="false"
          />
        )}
      </Stack>

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Typography variant="h-xs" color="brand.primary.dark_2">
          Localizações
        </Typography>

        {pivotData('location').length > 0 ? (
          <Box height={300}>
            <BarChart data={pivotData('location')} />
          </Box>
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de localizações para exibir."
            iconProps={{ name: 'chart-bar' }}
            shaped="false"
          />
        )}
      </Stack>

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Typography variant="h-xs" color="brand.primary.dark_2">
          Etapas de Ensino
        </Typography>

        {pivotData('educationStage').length > 0 ? (
          <Box height={300}>
            <BarChart data={pivotData('educationStage')} />
          </Box>
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de etapas de ensino para exibir."
            iconProps={{ name: 'chart-bar' }}
            shaped="false"
          />
        )}
      </Stack>

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Form
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          enableReinitialize={true}
        >
          <HStack>
            <Select
              selectRef={ref => (selectRefs.current.states = ref)}
              name="states"
              label="Filtrar por estado"
              placeholder="Todos"
              options={availableStateOptions}
              disabled={isStateDisabled}
              isMulti
              {...({ onChange: handleStatesChange } as any)}
            />
            <Select
              selectRef={ref => (selectRefs.current.cities = ref)}
              name="cities"
              label="Filtrar por Munícipio"
              placeholder="Todos"
              options={availableCityOptions}
              disabled={isCityGlobalDisabled || isCitiesLoading}
              isMulti
              {...(isCitiesLoading && { isLoading: true })}
              {...({ onChange: handleCitiesChange } as any)}
            />
          </HStack>
        </Form>

        {isCensusEducationStagesLoading ? (
          <Skeleton height={360} borderRadius="md" />
        ) : censusEducationStages &&
          censusEducationStages.length > 0 &&
          !censusEducationStages.every(
            yearData => yearData.educationData.length === 0
          ) ? (
          <SlidesDisplay
            items={pivotEducationStagesData().map(stage => ({
              title: stage.title,
              content: (
                <Box height={300}>
                  <BarChart data={stage.data} />
                </Box>
              ),
            }))}
          />
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de etapas de ensino para os filtros selecionados."
            iconProps={{ name: 'table' }}
            shaped="false"
          />
        )}
      </Stack>
    </Stack>
  )
}
